import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Storefront read seam → the backend's public `POST /catalog` custom API (IS_PUBLIC, no token).
 *
 * Body: { op, collection, find?, sort?, select?, skip?, limit?, getTotalCount?, slug? }
 *   op: 'query' (default) | 'get-by-slug' | 'count'. Allowed collections are whitelisted
 *   server-side: products | categories | collections | banners | reviews.
 *
 * Envelope: `{ success, statusCode, data }` where, under `data`:
 *   query        → { records: T[], totalCount }
 *   get-by-slug  → the single document (or null)
 *   count        → { count }
 */

export interface QueryOptions {
  find?: Record<string, unknown>;
  sort?: string;
  select?: string;
  skip?: number;
  limit?: number;
  getTotalCount?: boolean;
}

export interface QueryResult<T> {
  records: T[];
  totalCount: number;
}

interface AmEnvelope<T> {
  success?: boolean;
  statusCode?: number;
  data?: T;
}

@Injectable({ providedIn: 'root' })
export class CatalogService {
  private http = inject(HttpClient);
  private url = `${environment.customApiBase}/catalog`;

  /** List documents from a whitelisted collection. */
  query<T>(collection: string, opts: QueryOptions = {}): Observable<QueryResult<T>> {
    const body = {
      op: 'query',
      collection,
      find: opts.find ?? {},
      sort: opts.sort,
      select: opts.select,
      skip: opts.skip ?? 0,
      limit: opts.limit,
      getTotalCount: opts.getTotalCount ?? true,
    };
    return this.http.post<AmEnvelope<QueryResult<T>>>(this.url, body).pipe(
      map((res) => ({
        records: res?.data?.records ?? [],
        totalCount: res?.data?.totalCount ?? 0,
      })),
    );
  }

  /** Fetch a single document by its `slug` (or null if not found). */
  getBySlug<T>(collection: string, slug: string): Observable<T | null> {
    return this.http
      .post<AmEnvelope<T>>(this.url, { op: 'get-by-slug', collection, slug })
      .pipe(map((res) => res?.data ?? null));
  }

  /** Count documents matching `find`. */
  count(collection: string, find: Record<string, unknown> = {}): Observable<number> {
    return this.http
      .post<AmEnvelope<{ count: number }>>(this.url, { op: 'count', collection, find })
      .pipe(map((res) => res?.data?.count ?? 0));
  }
}
